import { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import api from '../../../services/api';
import { extractListData, formatDate } from '../../../utils/Utils';

// Task 31 — Sürekli Geri Bildirim FE ekranları (gönder / aldıklarım / ekibim /
// İK). Gerçek `ContinuousFeedbackController` rotaları (grep ile doğrulanmış,
// Task 15):
//   POST v1/continuous-feedback/my           {receiverEmployeeId, category, text?, isAnonymous}
//   GET  v1/continuous-feedback/my/received  (self — DÜZ liste, PaginatedList DEĞİL)
//   GET  v1/continuous-feedback/team?receiverEmployeeId[eq]&category[eq]&pageNumber&pageSize&orderBy&orderDir
//        (yönetici — PaginatedList; TeamFeedbackVisibleToManager flag'i kapalıysa
//        409 performance.feedback.teamViewDisabled — [ModuleAuthorize("PM","Read")])
//   GET  v1/continuous-feedback?receiverEmployeeId[eq]&category[eq]&pageNumber&pageSize&orderBy&orderDir
//        (İK — PaginatedList, [ModuleAuthorize("PM","Read")]; Görev 6/PMB-08'den
//        beri KAPSAMLI: enterpriseAdmin tüm kiracıyı, diğerleri kendi aldıkları
//        ∪ (bayrak açıksa) doğrudan raporlarını görür — 409 yerine sessiz süzme)
// ReceivedFeedbackDto TİP DÜZEYİNDE gönderen çalışan Guid'i taşımaz (yalnız
// isimli gönderimde dolu SenderName) — anonimlik garantisi FE'ye kadar taşınır.
// ReceiverEmployeeId ise bir Guid, isim alanı YOK — Team/HR sayfaları alıcı
// adını feedback-360/config'teki useEmployeeNames deseniyle (bağımsız kopya,
// farklı sayfa grubu — repo konvansiyonu) lazy çözer.

export const FEEDBACK_CATEGORIES = ['strength', 'development_area'];

// Kategori rozeti renkleri — repoda zaten kullanılan çift setlerle aynı
// (MyDevelopmentPlansPage/CALIBRATION_STATUS_COLORS'daki "olumlu"/"turuncu"
// tonları, yeni bir renk icat edilmedi).
export const CATEGORY_COLORS = {
  strength: { color: '#1B7A3E', bg: '#E8F7EE' },
  development_area: { color: '#C0501A', bg: '#FCE8D5' },
};

export const CategoryBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => CATEGORY_COLORS[p.$category]?.bg || '#F5F7FA'};
  color: ${(p) => CATEGORY_COLORS[p.$category]?.color || '#6c757d'};
`;

export function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      // EmployeeController yalnız firstName[like]/lastName[like]/fullName[like]
      // bind ediyor — `name[like]` HİÇBİR alana bağlanmıyor (no-op, sessizce
      // ilk 20 kaydı döndürüyordu). `fullName[like]` ad+soyad birleşiminde
      // arıyor (async çalışan seçicilerin repo genelindeki doğru deseni).
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const items = extractListData(res);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

export const employeeAsync = buildEmployeeAsync();

// Kendini hariç tutan çalışan seçici — SendFeedbackPage (alıcı) VE
// RequestFeedbackModal (hedef) AYNI dosya grubunda kullanır, bu yüzden TEK
// yardımcı paylaşılıyor (repo konvansiyonu yalnız FARKLI sayfa gruplarında
// bağımsız kopya ister, bkz. yukarıdaki buildEmployeeAsync yorumu — burada
// aynı klasör içinde iki tüketici olduğu için paylaşım bilinçli). Gerçek
// koruma her zaman backend'dedir (selfNotAllowed / performance.feedbackRequest.selfRequest).
export function buildSelfExcludedEmployeeAsync(excludeEmployeeId) {
  return {
    onSearch: async (q) => {
      const results = await employeeAsync.onSearch(q);
      if (!excludeEmployeeId) return results;
      return results.filter((r) => String(r.id) !== String(excludeEmployeeId));
    },
    onGetById: employeeAsync.onGetById,
  };
}

// Lazy id→ad çözümü (Team/HR listelerinde alıcı adı göstermek için) —
// feedback-360/config'teki useEmployeeNames'in bağımsız kopyası (aynı gerekçe:
// farklı sayfa grubu, repo konvansiyonu paylaşılan hook yerine bağımsız kopya).
export const useEmployeeNames = (ids) => {
  const [names, setNames] = useState({});
  const namesRef = useRef(names);
  namesRef.current = names;
  const idsKey = JSON.stringify([...new Set((ids || []).filter(Boolean))].sort());

  useEffect(() => {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    const missing = uniqueIds.filter((id) => !(id in namesRef.current));
    if (missing.length === 0) return undefined;

    let cancelled = false;
    Promise.allSettled(missing.map((id) => employeeAsync.onGetById(id)))
      .then((results) => {
        if (cancelled) return;
        setNames((current) => {
          const next = { ...current };
          missing.forEach((id, i) => {
            const r = results[i];
            next[id] = (r.status === 'fulfilled' && r.value?.name) || id;
          });
          return next;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return names;
};

// ── SendFeedbackPage ──────────────────────────────────────────────────────

export const useSendFeedbackApi = () => {
  const [sending, setSending] = useState(false);

  // payload: { receiverEmployeeId, category, text?, isAnonymous }
  const send = useCallback(async (payload) => {
    setSending(true);
    try {
      await api.post('/v1/continuous-feedback/my', payload);
      return true;
    } catch {
      return false;
    } finally {
      setSending(false);
    }
  }, []);

  return { send, sending };
};

// ── MyFeedbackPage (aldıklarım) ───────────────────────────────────────────
// GetMyReceivedAsync DÜZ bir liste döner (PaginatedList DEĞİL) — sayfalama yok.
export const useMyReceivedFeedbackApi = () => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/continuous-feedback/my/received')
      .then((res) => {
        if (cancelled) return;
        setData(res.data?.status ? (res.data.data || []) : []);
      })
      .catch(() => { if (!cancelled) setData([]); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  return { data, fetching, refresh };
};

// ── Geri bildirim talepleri ("Taleplerim" sekmesi) ────────────────────────
// Task 5 (spec 2026-08-08-pm-surekli-performans-faz-d-design.md §4, D3) —
// v1/feedback-requests (task-2-report.md §1): GET my İKİ AYRI LİSTE döner
// ({sent, received}) — OneOnOne'ın tek-liste+myRole deseninden BİLİNÇLİ
// FARKLI (Task 2'nin kendi kararı, FE'nin iki-sekme tasarımına birebir).

export const FEEDBACK_REQUEST_STATUS_COLORS = {
  pending: { color: '#C0501A', bg: '#FCE8D5' },
  completed: { color: '#16A34A', bg: '#DCFCE7' },
  declined: { color: '#E53935', bg: '#FEF2F2' },
  cancelled: { color: '#667', bg: '#F1F5F9' },
};

// CategoryBadge ile AYNI imza ($status prop + map lookup) — aynı dosyada
// zaten kurulu idiom, yeni bir görsel dil icat edilmedi.
export const RequestStatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => FEEDBACK_REQUEST_STATUS_COLORS[p.$status]?.bg || '#F5F7FA'};
  color: ${(p) => FEEDBACK_REQUEST_STATUS_COLORS[p.$status]?.color || '#6c757d'};
`;

// GET v1/feedback-requests/my — ResponseObjectModel.data = { sent, received }
// (MyFeedbackRequestsDto, task-2-report.md §1). create/cancel/decline BE
// yanıtını YERİNDE birleştirir (useOneOnOnesApi'nin try/catch + yerinde
// birleştirme deseniyle AYNI).
export const useFeedbackRequestsApi = () => {
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/feedback-requests/my')
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.status ? res.data.data : null;
        setSent(data?.sent || []);
        setReceived(data?.received || []);
      })
      .catch(() => { if (!cancelled) { setSent([]); setReceived([]); } })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  // POST body { targetEmployeeId, message? } — yanıt TAM FeedbackRequestDto,
  // gönderdiklerim listesinin BAŞINA eklenir.
  const createRequest = useCallback(async (payload) => {
    try {
      const res = await api.post('/v1/feedback-requests', payload);
      const created = res.data?.data;
      if (created) setSent((prev) => [created, ...prev]);
      return created || null;
    } catch {
      return null;
    }
  }, []);

  // Yalnız talep eden, yalnız pending (performance.feedbackRequest.notRequester/invalidState).
  const cancelRequest = useCallback(async (id) => {
    try {
      const res = await api.put(`/v1/feedback-requests/${id}/cancel`);
      const updated = res.data?.data;
      if (updated) setSent((prev) => prev.map((r) => (r.id === id ? updated : r)));
      return !!updated;
    } catch {
      return false;
    }
  }, []);

  // Yalnız hedef, yalnız pending, gerekçesiz (performance.feedbackRequest.notTarget/invalidState).
  const declineRequest = useCallback(async (id) => {
    try {
      const res = await api.put(`/v1/feedback-requests/${id}/decline`);
      const updated = res.data?.data;
      if (updated) setReceived((prev) => prev.map((r) => (r.id === id ? updated : r)));
      return !!updated;
    } catch {
      return false;
    }
  }, []);

  return {
    sent, received, fetching, refresh, createRequest, cancelRequest, declineRequest,
  };
};

// SendFeedbackPage `?requestId=` bağlamı — GET v1/feedback-requests/my
// 'received' listesinden requestId eşleşen PENDING kaydı bulur. Ayrı bir
// GetById ucu YOK (task-2-report.md §1) — bu yüzden liste yeniden okunur.
// Bulunamazsa (yabancı talep / az önce yanıtlanmış-reddedilmiş-iptal edilmiş)
// sessizce null döner, sayfa kilitsiz normal forma düşer.
export const useFeedbackRequestContext = (requestId) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(!!requestId);

  useEffect(() => {
    if (!requestId) {
      setRequest(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.get('/v1/feedback-requests/my')
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.status ? (res.data.data?.received || []) : [];
        setRequest(list.find((r) => r.id === requestId && r.status === 'pending') || null);
      })
      .catch(() => { if (!cancelled) setRequest(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [requestId]);

  return { request, loading };
};

// ── TeamFeedbackPage / HrFeedbackPage — ortak sayfalı+filtreli liste ──────
// scope: 'team' | 'all'. 'team' ucu TeamFeedbackVisibleToManager flag'i
// kapalıyken 409 döner — bu beklenen/normal bir durum olduğu için
// skipErrorToast ile bastırılır, `disabled` bayrağı sayfa içi bilgi metnine
// çevrilir (MyCalibrationPage'in notYetVisible desenindeki AYNI disiplin).
// 'all' (İK) ucunda böyle bir kapı yok — gerçek hatalar toast'la gösterilir.
export const useFeedbackListApi = (scope, queryParams) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [disabled, setDisabled] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const url = scope === 'team' ? '/v1/continuous-feedback/team' : '/v1/continuous-feedback';
  const paramsKey = JSON.stringify(queryParams || {});

  useEffect(() => {
    setFetching(true);
    // passErrorBody: interceptor GET hatalarını REDDETMEZ — normalde boş
    // EMPTY_RESPONSE ile resolve eder ve 409'un mesaj anahtarı kaybolur
    // (.catch asla çalışmaz). Bu bayrak gerçek hata gövdesini geçirir; flag
    // kapısı aşağıda mesaj anahtarından ayıklanır (bkz. services/api.js).
    api.get(url, { params: queryParams, skipErrorToast: scope === 'team', passErrorBody: scope === 'team' })
      .then((res) => {
        if (res.data?.status === false) {
          setData([]);
          setDisabled(scope === 'team' && res.data?.message === 'performance.feedback.teamViewDisabled');
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: queryParams?.pageSize || 10 });
          return;
        }
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setDisabled(false);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => { setData([]); setDisabled(false); })
      .finally(() => setFetching(false));
    // paramsKey bilinçli bağımlılık — bkz. period-goals/config'teki
    // useApiPagedList'teki aynı yorum (params her render'da yeni referans olabilir).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, paramsKey, scope, refreshTrigger]);

  return {
    data, fetching, pagination, disabled, refresh,
  };
};

// Team/HR DataTable kolonları — ortak (alıcı adı `names` lazy-cache'inden,
// gönderen isimli/anonim ayrımı DTO'dan doğrudan). Yalnız görüntüleme —
// backend `orderBy` yalnızca CreateDate'i anlamlı şekilde destekliyor
// (ListInternalAsync), bu yüzden sütun sıralaması kapalı.
export const buildFeedbackColumns = (t, data, names, lang) => [
  {
    name: 'receiverEmployeeId',
    label: t('performance.feedback.receiver'),
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => names[value] || value || '-',
    },
  },
  {
    name: 'category',
    label: t('performance.feedback.categoryLabel'),
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => (
        <CategoryBadge $category={value}>
          {t(`performance.feedback.category.${value}`, { defaultValue: value })}
        </CategoryBadge>
      ),
    },
  },
  {
    name: 'sender',
    label: t('performance.feedback.sender'),
    options: {
      filter: false,
      sort: false,
      customBodyRender: (_, tableMeta) => {
        const row = data[tableMeta.rowIndex];
        if (!row) return null;
        return row.isAnonymous ? t('performance.feedback.anonymous') : (row.senderName || '-');
      },
    },
  },
  {
    name: 'text',
    label: t('performance.feedback.text'),
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => value || '-',
    },
  },
  {
    name: 'createDate',
    label: t('performance.feedback.date'),
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => formatDate(value, lang),
    },
  },
];
